import React, { useState, useEffect } from "react";
import styled from "styled-components";
import RequestV2 from "api/RequestV2";
import Config from "utils/config";

import {
    Button,
    InputField,
    SuccessMessage,
    ErrorMessage,
} from "../../design-system";

import { Switch, FormControlLabel } from "@mui/material";
import { theme } from "../../design-system/theme";

const FormWrapper = styled.form`
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  padding-top: ${theme.spacing.xs};
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${theme.spacing.sm};
`;

const ToggleRow = styled.div`
  margin-top: ${theme.spacing.xs};
`;

export default function TidFormV2({ editData, onSuccess }) {
    const isEdit = Boolean(editData);

    const [formData, setFormData] = useState({
        tid: "",
        orgCode: "",
        isActive: true,
    });

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({
        tid: "",
        orgCode: "",
    });

    const validate = () => {
        const newErrors = {};

        if (!formData.tid) {
            newErrors.tid = "TID is required";
        } else if (!/^[A-Za-z0-9]+$/.test(formData.tid)) {
            newErrors.tid = "Only alphanumeric characters allowed";
        } else if (formData.tid.length > 50) {
            newErrors.tid = "Max 50 characters allowed";
        }

        if (!formData.orgCode) {
            newErrors.orgCode = "Org Code is required";
        } else if (!/^[A-Za-z0-9]+$/.test(formData.orgCode)) {
            newErrors.orgCode = "Only alphanumeric characters allowed";
        } else if (formData.orgCode.length > 50) {
            newErrors.orgCode = "Max 50 characters allowed";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (editData) {
            setFormData({
                tid: editData.tid || "",
                orgCode: editData.orgCode || "",
                isActive: editData.active ?? true,
            });
        }
    }, [editData]);

    const update = (key, value) => {
        if (value.length > 50) {
            setErrors((prev) => ({
                ...prev,
                [key]: "Maximum 50 characters allowed",
            }));
            return;
        }

        if (!/^[A-Za-z0-9]*$/.test(value)) {
            setErrors((prev) => ({
                ...prev,
                [key]: "Only alphanumeric characters allowed",
            }));
            return;
        }

        setFormData((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validate()) return;

        setLoading(true);
        setError("");
        setMessage("");

        const api = new RequestV2(
            "",
            () => {
                setLoading(false);
                setMessage(
                    isEdit ? "TID Updated Successfully" : "TID Created Successfully"
                );
                onSuccess();
            },
            (err) => {
                setLoading(false);
                setError(err?.message || "Failed");
            }
        );

        if (isEdit) {
            api.put(
                `${Config.apis.admin.TID_WHITELISTING}${editData?.id}`,
                formData
            );
        } else {
            api.post(Config.apis.admin.TID_WHITELISTING, formData);
        }
    };

    return (
        <FormWrapper onSubmit={handleSubmit}>
            {message && <SuccessMessage successMessage={message} />}
            {error && <ErrorMessage error={error} />}

            <InputField
                label="TID"
                value={formData.tid}
                onChange={(v) => update("tid", v)}
                error={errors.tid}
                helperText={
                    errors.tid || `${formData.tid.length}/50 characters`
                }
                required
            />

            <InputField
                label="Org Code"
                value={formData.orgCode}
                onChange={(v) => update("orgCode", v)}
                error={errors.orgCode}
                helperText={
                    errors.orgCode || `${formData.orgCode.length}/50 characters`
                }
                required
            />

            <ToggleRow>
                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.isActive}
                            onChange={(e) => update("isActive", e.target.checked)}
                        />
                    }
                    label="Active"
                />
            </ToggleRow>

            <ActionRow>
                <Button type="submit" loading={loading}>
                    {isEdit ? "Update" : "Create"}
                </Button>
            </ActionRow>
        </FormWrapper>
    );
}
